<template>
  <div class="desktop-header-wrapper">
    <WorkBaseHeaderBar>
      <template #logo>
        <div 
          v-if="useCustomLogo == true"
          @click="goToNavigation"
        >
          <img
            v-if="isProdMode && customLogoProdPath != undefined"
            :src="require(`../../assets/${customerInstance}/header/${customLogoProdPath}`)"
            class="logo"
          >
          <img
            v-else-if="customLogoTestPath !== undefined && customerInstance !== undefined"
            :src="require(`../../assets/${customerInstance}/header/${customLogoTestPath}`)"
            class="logo"
          >
        </div>
        <div 
          v-else
          @click="goToNavigation"
        >
          <img
            v-if="isProdMode"
            src="../../assets/header/work_logo_white.svg"
            class="logo"
          >
          <img
            v-else
            src="../../assets/header/work_logo_white_test.svg"
            class="logo"
          >
        </div>
      </template>

      <template #prepend-actions>
        <w-dashboard-button
          v-if="showDashboard"
          class="v-btn--flat"
          icon
          color="white"
          :outlined="false"
          @click="goToDashboard"
        >
          <template #tooltip>
            {{ $t('Common.dashboard') }}
          </template>
        </w-dashboard-button>

        <w-module-overview-button
          class="v-btn--flat"
          icon
          color="white"
          :outlined="false"
          @click="goToNavigation"
        >
          <template #tooltip>
            {{ $t('Common.navigation') }}
          </template>
        </w-module-overview-button>

        <w-tooltip>
          <template #tooltip-activator="{ on, attrs }">
            <WorkHeaderOpenWindowsBadge
              :count-of-active-modules="countOfActiveModules"
              offset-x="22"
              offset-y="22"
            >
              <template #content="{ icon }">
                <w-btn
                  class="v-btn--flat"
                  v-bind="attrs"
                  icon
                  color="white"
                  :outlined="false"
                  v-on="on"
                  @click="goToOpenWindows"
                >
                  <w-icon color="white">
                    $wHistory
                  </w-icon>
                </w-btn>
              </template>
            </WorkHeaderOpenWindowsBadge>
          </template>
          <template #tooltip>
            {{ $t('Components.sentences.openWindows') }}
          </template>
        </w-tooltip>
      </template>

      <template #search-action>
        <WorkHeaderSearchField />
      </template>

      <template #user-information>
        <v-menu offset-y>
          <template #activator="{ on }">
            <div v-on="on">
              <a
                href="#"
                class="desktop-avatar"
              >
                <v-avatar size="35">
                  <v-img :src="currentUserImage" />
                </v-avatar>
                <span class="avatar-name">{{
                  displayName
                }}</span>
              </a>
            </div>
          </template>
          <v-list class="custom-list">
            <v-list-item @click="goToUserProfile()">
              <v-list-item-title class="custom-title">
                {{ $t('Common.profile') + '/' + $t('Common.settings') }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item @click="showDeputyModal()">
              <v-list-item-title
                v-if="deputyExists"
                class="custom-title"
              >
                {{
                  $t('Deputies.change')
                }}
              </v-list-item-title>
              <v-list-item-title
                v-else
                class="custom-title"
              >
                {{
                  $t('Deputies.login')
                }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="deputyExists"
              @click="logoutAsDeputy()"
            >
              <v-list-item-title class="custom-title">
                {{
                  $t('Deputies.logout')
                }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>

      <template #append-actions>
        <w-help-button
          class="v-btn--flat"
          icon
          color="white"
          :outlined="false"
          @click="showHelpWindow"
        >
          <template #tooltip>
            {{ $t('Common.help') }}
          </template>
        </w-help-button>

        <w-message-add-button
          v-if="!disableMessageAddButton && userHasRightToWriteMessages"
          class="v-btn--flat"
          color="white"
          @click="navigateToCreateMessageForm"
        >
          <template #tooltip>
            {{ $t('Common.newMessage') }}
          </template>
        </w-message-add-button>

        <WorkNotificationMenu />
        <w-logout-button
          class="v-btn--flat"
          icon
          color="white"
          :outlined="false"
          @click="logout"
        >
          <template #tooltip>
            {{ $t('Common.logout') }}
          </template>
        </w-logout-button>
      </template>
    </WorkBaseHeaderBar>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import WorkBaseHeaderBar from './WorkBaseHeaderBar';
import WorkHeaderSearchField from './components/WorkHeaderSearchField';
import WorkHeaderOpenWindowsBadge from './components/WorkHeaderOpenWindowsBadge';
import WorkNotificationMenu from '@/components/header_components/components/WorkNotificationMenu';
import dynamicData from '@/mixins/api_data/dynamic-data-mixin';

const DEFAULT_IMAGE = require('@/assets/upload/default_profil_image.jpg');

export default {
  name: 'WorkDesktopHeaderBar',
  components: {
    WorkNotificationMenu,
    WorkHeaderOpenWindowsBadge,
    WorkHeaderSearchField,
    WorkBaseHeaderBar,
  },
  props: {
    showDashboard: { type: Boolean, default: true },
    // Help
    showHelpWindow: { type: Function, required: true },
    // Go To
    goToNavigation: { type: Function, required: true },
    goToOpenWindows: { type: Function, required: true },
    goToDashboard: { type: Function, required: true },
    navigateToCreateMessageForm: { type: Function, required: true },
    // Theme
    toggleTheme: { type: Function, required: true },
    // settings
    goToUserProfile: { type: Function, required: true },
    // Acting User
    showDeputyModal: { type: Function, required: true },
    logoutAsDeputy: { type: Function, required: true },
    // logout
    logout: { type: Function, required: true },
    // data
    deputyExists: { type: Number },
    countOfActiveModules: { type: Number, required: true },
    languages: { type: Array, required: true },
    useCustomLogo: { type: Boolean, default: false},
    customLogoTestPath: { type: String, default: undefined},
    customLogoProdPath: { type: String, default: undefined},
    customerInstance: {type: String, default: undefined},
    disableMessageAddButton: { type: Boolean, default: false },
  },
  mixins: [
    dynamicData('systeminfo', 'system/information'),
    dynamicData(
      'hasRightToWriteMessages', 'rights/actingUserHasRight/WORK_Nachrichten_Schreibend',
      (response) => response?.data,
      {},
      false
    ),
  ],
  computed: {
    ...mapState('actingUser', ['actingUserDetails']),
    isProdMode() {
      return this.systeminfo.mode === 'prod';
    },
    displayName() {
      let displayName = this.actingUserDetails.fullname;
      if (!displayName) {
        displayName = this.actingUserDetails.username;
      }
      return `${displayName}${this.deputyExists ? ` (${this.$t('Common.deputy')})` : ''}`;
    },
    currentUserImage(){
      return this.actingUserDetails?.imgSrc ?? DEFAULT_IMAGE
    },
    userHasRightToWriteMessages() {
      return this.hasRightToWriteMessages;
    }
 },
};
</script>

<style lang="scss">
$logo-size: 3.75rem;

.desktop-header-wrapper .work-header-bar {
  .desktop-avatar {
    text-decoration: none;
    font-weight: bold;
    display: flex;
    flex-direction: row;
    align-items: center;

    .avatar-name {
      margin-left: 0.5rem;
      vertical-align: middle;
      color: $h1-color;
    }

    @media #{map-get($display-breakpoints, 'md-and-down')} {
      .avatar-name {
        display: none;
      }
    }
  }
  .work-logo {
    height: $logo-size;

    .logo {
      height: $logo-size;
      width: $logo-size;
    }
  }
}
</style>
